import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'
import {
  API_ACTIVATE_LINK,
  API_ADD_TAGS,
  API_CREATE_LINK,
  API_DEACTIVATE_LINK,
  API_DELETE_LINK,
  API_EDIT_LINK,
  API_GET_CLICKS_LINK,
  API_GET_CLICKS_SCANS_PER_DAY_BY_URL,
  API_REPORT_LINK,
  API_REPORT_LINKS,
  API_REPORT_TOTAL_CLICKS,
  API_REPORT_TOTAL_LINKS,
  API_UPDATE_PLAN,
  API_USER_TAGS,
  SERVICE_AUTH_KEY,
} from '../../../helpers/config/config'

const header = (token: string | undefined) => ({
  headers: {
    Authorization: `Bearer ${token}`,
    'service-auth-key': `${SERVICE_AUTH_KEY}`,
  },
})

export const createLink = createAsyncThunk(
  'url/createLink',
  async (
    { token, body }: { token: string | undefined; body: any },
    thunkAPI
  ) => {
    try {
      const { data } = await axios.post(
        `${API_CREATE_LINK}`,
        body,
        header(token)
      )

      return data
    } catch (err) {
      return thunkAPI.rejectWithValue({
        errorMessage: err,
      })
    }
  }
)

export const editLink = createAsyncThunk(
  'url/editLink',
  async (
    { token, body }: { token: string | undefined; body: any },
    thunkAPI
  ) => {
    try {
      const { data, status } = await axios.post(
        `${API_EDIT_LINK}`,
        body,
        header(token)
      )

      return data
    } catch (err) {
      return thunkAPI.rejectWithValue({
        errorMessage: err,
      })
    }
  }
)

export const deleteLink = createAsyncThunk(
  'url/deleteLink',
  async (
    { token, urlId }: { token: string | undefined; urlId: string },
    thunkAPI
  ) => {
    try {
      const { data } = await axios.post(
        `${API_DELETE_LINK}/${urlId}/delete`,
        {},
        header(token)
      )
      return data
    } catch (err) {
      return thunkAPI.rejectWithValue({
        errorMessage: err,
      })
    }
  }
)

export const getLinks = createAsyncThunk(
  'url/getLinks',
  async (
    {
      token,
      page,
      hasqr,
      status,
      fromDate,
      toDate,
      tag,
    }: {
      token: string | undefined
      page: number | undefined
      hasqr: number | undefined
      status: number | undefined
      fromDate: string | undefined
      toDate: string | undefined
      tag: string[] | [] | undefined
    },
    thunkAPI
  ) => {
    try {
      interface RequestParams {
        hasQr?: number
        page?: number
        status?: number
        fromDate?: string
        toDate?: string
        tag?: string[]
      }

      let params: RequestParams = {
        hasQr: hasqr,
        page: page,
        status: status,
        fromDate: fromDate,
        toDate: toDate,
      }

      if (tag && tag.length > 0 && Number(tag[0]) !== 0) {
        params.tag = tag
      }

      const response = await axios.get(API_REPORT_LINKS, {
        headers: header(token).headers,
        params: params,
      })

      const data = response.data

      return data
    } catch (err) {
      return thunkAPI.rejectWithValue({
        errorMessage: err,
      })
    }
  }
)

export const getTotalLinks = createAsyncThunk(
  'url/getTotalLinks',
  async (
    {
      token,
      hasqr,
      status,
      fromDate,
      toDate,
      tag,
    }: {
      token: string | undefined
      hasqr: number | undefined
      status: number | undefined
      fromDate: string | undefined
      toDate: string | undefined
      tag: string[] | []
    },
    thunkAPI
  ) => {
    try {
      interface RequestParams {
        hasQr?: number
        status?: number
        fromDate?: string
        toDate?: string
        tag?: string[]
      }

      let params: RequestParams = {
        hasQr: hasqr,
        status: status,
        fromDate: fromDate,
        toDate: toDate,
      }

      if (tag.length > 0 && Number(tag[0]) !== 0) {
        params.tag = tag
      }

      const response = await axios.get(API_REPORT_TOTAL_LINKS, {
        headers: header(token).headers,
        params: params,
      })

      const data = response.data

      return data
    } catch (err) {
      return thunkAPI.rejectWithValue({
        errorMessage: err,
      })
    }
  }
)

export const getLink = createAsyncThunk(
  'url/getLink',
  async (
    { token, id }: { token: string | undefined; id: string },
    thunkAPI
  ) => {
    try {
      const response = await axios.get(
        `${API_REPORT_LINK}/${id}`,
        header(token)
      )

      const data = response.data
      return data
    } catch (err) {
      return thunkAPI.rejectWithValue({
        errorMessage: err,
      })
    }
  }
)

export const getTotalTracking = createAsyncThunk(
  'url/getTotalTracking',
  async (
    {
      token,
      fromDate,
      toDate,
    }: {
      token: string | undefined
      fromDate: string | undefined
      toDate: string | undefined
    },
    thunkAPI
  ) => {
    try {
      const config = {
        headers: header(token).headers,
        params: {
          fromDate: fromDate,
          toDate: toDate,
        },
      }

      const response = await axios.get(`${API_REPORT_TOTAL_CLICKS}`, config)

      const data = response.data.data

      return data
    } catch (err) {
      return thunkAPI.rejectWithValue({
        errorMessage: err,
      })
    }
  }
)

export const getUserTags = createAsyncThunk(
  'url/getUserTags',
  async ({ token }: { token: string | undefined }, thunkAPI) => {
    try {
      const response = await axios.get(`${API_USER_TAGS}`, header(token))
      const data = response?.data

      return data
    } catch (err) {
      return thunkAPI.rejectWithValue({
        errorMessage: err,
      })
    }
  }
)

export const postAddTags = createAsyncThunk(
  'url/postAddTags',
  async (
    { token, body }: { token: string | undefined; body: any },
    thunkAPI
  ) => {
    try {
      const { data } = await axios.post(`${API_ADD_TAGS}`, body, header(token))

      return data
    } catch (err) {
      return thunkAPI.rejectWithValue({
        errorMessage: err,
      })
    }
  }
)

export const postActivateLinks = createAsyncThunk(
  'url/postActivateLinks',
  async (
    { token, body }: { token: string | undefined; body: any },
    thunkAPI
  ) => {
    try {
      const { data } = await axios.post(
        `${API_ACTIVATE_LINK}`,
        body,
        header(token)
      )

      return data
    } catch (err) {
      return thunkAPI.rejectWithValue({
        errorMessage: err,
      })
    }
  }
)

export const postDeactivateLinks = createAsyncThunk(
  'url/postDeactivateLinks',
  async (
    { token, body }: { token: string | undefined; body: any },
    thunkAPI
  ) => {
    try {
      const { data } = await axios.post(
        `${API_DEACTIVATE_LINK}`,
        body,
        header(token)
      )

      return data
    } catch (err) {
      return thunkAPI.rejectWithValue({
        errorMessage: err,
      })
    }
  }
)

export const getLinkClicks = createAsyncThunk(
  'url/getLinkClicks',
  async (
    {
      token,
      urlId,
      page,
      from,
      to,
      fromQr,
    }: {
      token: string | undefined
      urlId: string | undefined
      page: number | undefined
      from: string | undefined
      to: string | undefined
      fromQr: number | undefined
    },
    thunkAPI
  ) => {
    try {
      const config = {
        headers: header(token).headers,
        params: {
          page: page,
          fromDate: from,
          toDate: to,
          fromQr: fromQr,
        },
      }

      const response = await axios.get(
        `${API_GET_CLICKS_LINK}/${urlId}`,
        config
      )
      const data = response.data

      return data
    } catch (err) {
      return thunkAPI.rejectWithValue({
        errorMessage: err,
      })
    }
  }
)

export const getScansPerDay = createAsyncThunk(
  'url/getScansPerDay',
  async (
    {
      token,
      urlId,
      from,
      to,
    }: {
      token: string | undefined
      urlId: string | undefined
      from: string | undefined
      to: string | undefined
    },
    thunkAPI
  ) => {
    try {
      const config = {
        headers: header(token).headers,
        params: {
          fromDate: from,
          toDate: to,
        },
      }

      const response = await axios.get(
        `${API_GET_CLICKS_SCANS_PER_DAY_BY_URL}/${urlId}/qr-scanned/per-day`,
        config
      )

      const data = response.data

      return data
    } catch (err) {
      return thunkAPI.rejectWithValue({
        errorMessage: err,
      })
    }
  }
)

export const getClicksPerDay = createAsyncThunk(
  'url/getClicksPerDay',
  async (
    {
      token,
      urlId,
      from,
      to,
    }: {
      token: string | undefined
      urlId: string | undefined
      from: string | undefined
      to: string | undefined
    },
    thunkAPI
  ) => {
    try {
      const config = {
        headers: header(token).headers,
        params: {
          fromDate: from,
          toDate: to,
        },
      }

      const response = await axios.get(
        `${API_GET_CLICKS_SCANS_PER_DAY_BY_URL}/${urlId}/clicks/per-day`,
        config
      )
      const data = response.data

      return data
    } catch (err) {
      return thunkAPI.rejectWithValue({
        errorMessage: err,
      })
    }
  }
)

export const changePlan = createAsyncThunk(
  'url/changePlan',
  async (
    { token, body }: { token: string | undefined; body: any },
    thunkAPI
  ) => {
    try {
      const { data, status } = await axios.post(
        `${API_UPDATE_PLAN}`,
        body,
        header(token)
      )

      return data
    } catch (err) {
      return thunkAPI.rejectWithValue({
        errorMessage: err,
      })
    }
  }
)

interface UrlResponse {
  success: boolean
  data: any
}

interface DataState {
  data: UrlResponse
  fetchingData: string
  error: string | null
}

const initialState: DataState = {
  data: { success: false, data: {} },
  fetchingData: 'NO_DATA',
  error: null,
}

export const url = createSlice({
  name: 'url',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createLink.pending, (state) => {
        state.fetchingData = 'NO_DATA'
        state.data = initialState.data
        state.error = null
      })
      .addCase(createLink.fulfilled, (state: DataState, action) => {
        state.fetchingData = 'FETCHED'
        state.data = action.payload
        state.error = null
      })
      .addCase(createLink.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload
      })
      .addCase(getLinks.pending, (state: DataState) => {
        state.fetchingData = 'NO_DATA'
        state.data = initialState.data
        state.error = null
      })
      .addCase(
        getLinks.fulfilled,
        (state: DataState, action: PayloadAction<UrlResponse>) => {
          state.fetchingData = 'FETCHED'
          state.data = action.payload
          state.error = null
        }
      )
      .addCase(
        getLinks.rejected,
        (state: DataState, action: PayloadAction<any>) => {
          state.error = action.payload
        }
      )
      .addCase(getLink.pending, (state: DataState) => {
        state.fetchingData = 'NO_DATA'
        state.data = initialState.data
        state.error = null
      })
      .addCase(
        getLink.fulfilled,
        (state: DataState, action: PayloadAction<UrlResponse>) => {
          state.fetchingData = 'FETCHED'
          state.data = action.payload
          state.error = null
        }
      )
      .addCase(
        getLink.rejected,
        (state: DataState, action: PayloadAction<any>) => {
          state.error = action.payload
        }
      )
      .addCase(editLink.pending, (state) => {
        state.fetchingData = 'NO_DATA'
        state.data = initialState.data
        state.error = null
      })
      .addCase(editLink.fulfilled, (state: DataState, action) => {
        state.fetchingData = 'FETCHED'
        state.data = action.payload
        state.error = null
      })
      .addCase(editLink.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload
      })
      .addCase(deleteLink.pending, (state) => {
        state.fetchingData = 'NO_DATA'
        state.data = initialState.data
        state.error = null
      })
      .addCase(deleteLink.fulfilled, (state: DataState, action) => {
        state.fetchingData = 'FETCHED'
        state.data = action.payload
        state.error = null
      })
      .addCase(deleteLink.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload
      })
      .addCase(postAddTags.pending, (state) => {
        state.fetchingData = 'NO_DATA'
        state.data = initialState.data
        state.error = null
      })
      .addCase(postAddTags.fulfilled, (state: DataState, action) => {
        state.fetchingData = 'FETCHED'
        state.data = action.payload
        state.error = null
      })
      .addCase(postAddTags.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload
      })
      .addCase(postActivateLinks.pending, (state) => {
        state.fetchingData = 'NO_DATA'
        state.data = initialState.data
        state.error = null
      })
      .addCase(postActivateLinks.fulfilled, (state: DataState, action) => {
        state.fetchingData = 'FETCHED'
        state.data = action.payload
        state.error = null
      })
      .addCase(
        postActivateLinks.rejected,
        (state, action: PayloadAction<any>) => {
          state.error = action.payload
        }
      )
      .addCase(postDeactivateLinks.pending, (state) => {
        state.fetchingData = 'NO_DATA'
        state.data = initialState.data
        state.error = null
      })
      .addCase(postDeactivateLinks.fulfilled, (state: DataState, action) => {
        state.fetchingData = 'FETCHED'
        state.data = action.payload
        state.error = null
      })
      .addCase(
        postDeactivateLinks.rejected,
        (state, action: PayloadAction<any>) => {
          state.error = action.payload
        }
      )
      .addCase(getTotalLinks.pending, (state) => {
        state.fetchingData = 'NO_DATA'
        state.data = initialState.data
        state.error = null
      })
      .addCase(getTotalLinks.fulfilled, (state: DataState, action) => {
        state.fetchingData = 'FETCHED'
        state.data = action.payload
        state.error = null
      })
      .addCase(getTotalLinks.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload
      })
      .addCase(getLinkClicks.pending, (state) => {
        state.fetchingData = 'NO_DATA'
        state.data = initialState.data
        state.error = null
      })
      .addCase(getLinkClicks.fulfilled, (state: DataState, action) => {
        state.fetchingData = 'FETCHED'
        state.data = action.payload
        state.error = null
      })
      .addCase(getLinkClicks.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload
      })
      .addCase(getClicksPerDay.pending, (state) => {
        state.fetchingData = 'NO_DATA'
        state.data = initialState.data
        state.error = null
      })
      .addCase(getClicksPerDay.fulfilled, (state: DataState, action) => {
        state.fetchingData = 'FETCHED'
        state.data = action.payload
        state.error = null
      })
      .addCase(
        getClicksPerDay.rejected,
        (state, action: PayloadAction<any>) => {
          state.error = action.payload
        }
      )
      .addCase(getScansPerDay.pending, (state) => {
        state.fetchingData = 'NO_DATA'
        state.data = initialState.data
        state.error = null
      })
      .addCase(getScansPerDay.fulfilled, (state: DataState, action) => {
        state.fetchingData = 'FETCHED'
        state.data = action.payload
        state.error = null
      })
      .addCase(getScansPerDay.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload
      })
      .addCase(getTotalTracking.pending, (state) => {
        state.fetchingData = 'NO_DATA'
        state.data = initialState.data
        state.error = null
      })
      .addCase(getTotalTracking.fulfilled, (state: DataState, action) => {
        state.fetchingData = 'FETCHED'
        state.data = action.payload
        state.error = null
      })
      .addCase(
        getTotalTracking.rejected,
        (state, action: PayloadAction<any>) => {
          state.error = action.payload
        }
      )
      .addCase(changePlan.pending, (state) => {
        state.fetchingData = 'FETCHING'
        state.data = initialState.data
        state.error = null
      })
      .addCase(changePlan.fulfilled, (state: DataState, action) => {
        state.fetchingData = 'FETCHED'
        state.data = action.payload
        state.error = null
      })
      .addCase(changePlan.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload
      })
  },
})

export default url.reducer
